import React, { useState } from "react";
import {
  Typography,
  Box,
  IconButton,
  TextField,
  InputAdornment,
  Divider,
  Collapse,
  Checkbox,
  Tooltip,
  Button,
  Badge,
  alpha,
} from "@mui/material";
import {
  RestartAlt as RestartAltIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  PushPin as PushPinIcon,
  PushPinOutlined as PushPinOutlinedIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  IndeterminateCheckBox as IndeterminateCheckBoxIcon,
  SelectAll as SelectAllIcon,
} from "@mui/icons-material";
import {
  ItemSelectionPanel as Panel,
  PanelHeader,
  PanelContent,
} from "./styles";
import styled from "styled-components";

// ✅ 스타일 컴포넌트 (기존과 동일)
const SelectedSection = styled.div`
  background: ${props => alpha('#1976d2', 0.05)};
  border: 1px solid ${props => alpha('#1976d2', 0.2)};
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
  max-height: 150px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #1976d2;
    border-radius: 3px;
  }
`;

const SelectedChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  margin: 4px;
  background: #1976d2;
  color: white;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #1565c0;
    transform: scale(0.98);
  }

  svg {
    font-size: 16px;
    opacity: 0.8;
    transition: opacity 0.2s;

    &:hover {
      opacity: 1;
    }
  }
`;

const CategorySection = styled.div`
  margin-bottom: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  background: white;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const CategoryHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${props => props.$isExpanded ? alpha('#1976d2', 0.08) : '#f9f9f9'};
  cursor: pointer;
  transition: all 0.2s;
  border-bottom: ${props => props.$isExpanded ? '1px solid #e0e0e0' : 'none'};

  &:hover {
    background: ${props => props.$isExpanded ? alpha('#1976d2', 0.12) : '#f0f0f0'};
  }
`;

const SingleItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: ${props => props.$isSelected ? alpha('#1976d2', 0.1) : 'white'};
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid ${props => props.$isSelected ? '#1976d2' : '#e0e0e0'};
  border-radius: 8px;
  margin-bottom: 8px;

  &:hover {
    background: ${props => props.$isSelected ? alpha('#1976d2', 0.15) : alpha('#1976d2', 0.05)};
    border-color: #1976d2;
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SingleItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const CategoryTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px;
  padding: 12px;
  background: #fafafa;
`;

const ItemCard = styled.div`
  padding: 10px 14px;
  border: 1px solid ${props => props.$isSelected ? '#1976d2' : '#e0e0e0'};
  background: ${props => props.$isSelected ? alpha('#1976d2', 0.1) : 'white'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 13px;
  font-weight: ${props => props.$isSelected ? 600 : 400};
  color: ${props => props.$isSelected ? '#1565c0' : '#333'};
  text-align: center;
  user-select: none;

  &:hover {
    border-color: #1976d2;
    background: ${props => props.$isSelected ? alpha('#1976d2', 0.15) : alpha('#1976d2', 0.05)};
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
`;

const QuickActions = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

function ItemSelectionPanel({
  isOpen = false,
  onClose = () => {},
  selectedItems = [],
  onToggleItem = () => {},
  filteredCategories = [],
  getFilteredItemsInCategory = () => [],
  expandedCategories = {},
  onToggleCategory = () => {},
  onSelectAllInCategory = () => {},
  onClearAllSelections = () => {},
  searchTerm = "",
  onSearchChange = () => {},
  onClearSearch = () => {},
  getItemName = (id) => id || "Unknown",
  isPinned = false,
  onTogglePin = () => {},
  itemCategories = {},
}) {
  const safeSelectedItems = Array.isArray(selectedItems) ? selectedItems : [];
  const safeFilteredCategories = Array.isArray(filteredCategories) ? filteredCategories : [];
  const safeExpandedCategories = expandedCategories || {};
  const safeItemCategories = itemCategories || {};
  const safeSearchTerm = searchTerm || "";

  const getSafeItemName = (itemId) => {
    try {
      if (!itemId) return "Unknown";
      const name = getItemName(itemId);
      return name || itemId || "Unknown";
    } catch (error) {
      console.error("Error getting item name:", error);
      return itemId || "Unknown";
    }
  };

  const getSafeFilteredItems = (category) => {
    try {
      if (!category) return [];
      const items = getFilteredItemsInCategory(category);
      return Array.isArray(items) ? items : [];
    } catch (error) {
      console.error("Error getting filtered items:", error);
      return [];
    }
  };

  const handleSingleItemToggle = (itemId) => {
    if (!itemId) return;
    onToggleItem(itemId);
  };

  // ✅ 전체 선택/해제 핸들러
  const handleSelectAll = () => {
    const allItems = [];
    Object.values(safeItemCategories).forEach(items => {
      if (Array.isArray(items)) {
        allItems.push(...items);
      }
    });
    
    if (allItems.length === 0) return;
    
    const allSelected = allItems.every(item => safeSelectedItems.includes(item));
    
    if (allSelected) {
      // 모두 선택되어 있으면 전체 해제
      onClearAllSelections();
    } else {
      // 아니면 전체 선택
      allItems.forEach(item => {
        if (item && !safeSelectedItems.includes(item)) {
          onToggleItem(item);
        }
      });
    }
  };

  // ✅ 전체 선택 여부 확인
  const isAllSelected = () => {
    const allItems = [];
    Object.values(safeItemCategories).forEach(items => {
      if (Array.isArray(items)) {
        allItems.push(...items);
      }
    });
    return allItems.length > 0 && allItems.every(item => safeSelectedItems.includes(item));
  };

  return (
    <Panel $isOpen={isOpen} className="item-panel">
      <PanelHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '16px' }}>
            품명 선택
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title={isPinned ? "고정 해제" : "패널 고정"}>
            <IconButton size="small" onClick={onTogglePin}>
              {isPinned ? (
                <PushPinIcon fontSize="small" color="primary" />
              ) : (
                <PushPinOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="모두 초기화">
            <IconButton 
              size="small" 
              onClick={onClearAllSelections}
              disabled={safeSelectedItems.length === 0}
            >
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </PanelHeader>

      <PanelContent>
        <TextField
          placeholder="품명 검색..."
          value={safeSearchTerm}
          onChange={(e) => onSearchChange(e.target.value || "")}
          size="small"
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: safeSearchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={onClearSearch}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* ✅ 전체 선택 버튼 */}
        {safeFilteredCategories.length > 0 && (
          <QuickActions>
            <Button
              size="small"
              variant="outlined"
              startIcon={isAllSelected() ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
              onClick={handleSelectAll}
              fullWidth
            >
              {isAllSelected() ? '전체 해제' : '전체 선택'}
            </Button>
          </QuickActions>
        )}

        {safeSelectedItems.length > 0 && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={600} color="primary">
                선택된 품명 ({safeSelectedItems.length})
              </Typography>
              <Button
                size="small"
                onClick={onClearAllSelections}
                sx={{ fontSize: '12px', minWidth: 'auto', px: 1 }}
              >
                전체 해제
              </Button>
            </Box>
            <SelectedSection>
              {safeSelectedItems.map((itemId) => {
                if (!itemId) return null;
                return (
                  <SelectedChip key={itemId}>
                    {getSafeItemName(itemId)}
                    <CloseIcon
                      fontSize="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleItem(itemId);
                      }}
                    />
                  </SelectedChip>
                );
              })}
            </SelectedSection>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          전체 품명 목록
        </Typography>

        {safeFilteredCategories.length === 0 ? (
          <EmptyState>
            <SearchIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
            <Typography>
              {safeSearchTerm ? "검색 결과가 없습니다" : "품명이 없습니다"}
            </Typography>
          </EmptyState>
        ) : (
          safeFilteredCategories.map((category) => {
            if (!category) return null;

            const filteredItems = getSafeFilteredItems(category);
            
            if (filteredItems.length === 0) return null;

            if (filteredItems.length === 1) {
              const singleItemId = filteredItems[0];
              const isSelected = safeSelectedItems.includes(singleItemId);
              
              return (
                <SingleItemRow
                  key={category}
                  $isSelected={isSelected}
                  onClick={() => handleSingleItemToggle(singleItemId)}
                >
                  <SingleItemContent>
                    {isSelected ? (
                      <CheckBoxIcon fontSize="small" sx={{ color: '#1976d2' }} />
                    ) : (
                      <CheckBoxOutlineBlankIcon fontSize="small" sx={{ color: '#999' }} />
                    )}
                    <Box>
                      <Typography 
                        variant="body2" 
                        fontWeight={isSelected ? 600 : 500}
                        sx={{ color: isSelected ? '#1565c0' : '#333' }}
                      >
                        {category}
                      </Typography>
                    </Box>
                  </SingleItemContent>
                  <Tooltip title={isSelected ? "선택 해제" : "선택"}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSingleItemToggle(singleItemId);
                      }}
                      sx={{ color: isSelected ? '#1976d2' : 'inherit' }}
                    >
                      {isSelected ? (
                        <CheckBoxIcon fontSize="small" />
                      ) : (
                        <CheckBoxOutlineBlankIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </SingleItemRow>
              );
            }

            const categorySelected = filteredItems.every((itemId) =>
              safeSelectedItems.includes(itemId)
            );
            const someSelected = filteredItems.some((itemId) =>
              safeSelectedItems.includes(itemId)
            );
            const isExpanded = safeExpandedCategories[category] || false;

            return (
              <CategorySection key={category}>
                <CategoryHeaderRow
                  $isExpanded={isExpanded}
                  onClick={() => onToggleCategory(category)}
                >
                  <CategoryTitle>
                    {isExpanded ? (
                      <ExpandMoreIcon fontSize="small" color="primary" />
                    ) : (
                      <ChevronRightIcon fontSize="small" />
                    )}
                    <Typography variant="body2" fontWeight={600}>
                      {category}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({filteredItems.length})
                    </Typography>
                  </CategoryTitle>

                  <Tooltip title={categorySelected ? "전체 해제" : "전체 선택"}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectAllInCategory(category, categorySelected);
                      }}
                      sx={{
                        color: categorySelected ? '#1976d2' : someSelected ? '#1976d2' : 'inherit'
                      }}
                    >
                      {categorySelected ? (
                        <CheckBoxIcon fontSize="small" />
                      ) : someSelected ? (
                        <IndeterminateCheckBoxIcon fontSize="small" />
                      ) : (
                        <CheckBoxOutlineBlankIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </CategoryHeaderRow>

                <Collapse in={isExpanded}>
                  <ItemsGrid>
                    {filteredItems.map((itemId) => {
                      if (!itemId) return null;
                      return (
                        <ItemCard
                          key={itemId}
                          $isSelected={safeSelectedItems.includes(itemId)}
                          onClick={() => onToggleItem(itemId)}
                        >
                          {getSafeItemName(itemId)}
                        </ItemCard>
                      );
                    })}
                  </ItemsGrid>
                </Collapse>
              </CategorySection>
            );
          })
        )}
      </PanelContent>
    </Panel>
  );
}

export default ItemSelectionPanel;
